import type ApplicationModel from 'components/framework/ApplicationModel';
import Fragment from '../../Fragment';
import CustomFieldValue from '../CustomFieldValue';

export interface HasCustomFieldValues {
  getCustomFieldValue(key: string): CustomFieldValue | undefined;
  setLocalCustomFieldValue(key: string, value: any): CustomFieldValue;
  setCustomFieldValue(key: string, value: any): Promise<void>;
}

export function HasCustomFieldValues(originalClass: {
  new (...args: any[]): ApplicationModel & {
    id: string;
    customFieldValues: CustomFieldValue[];
  };
}): any {
  return class extends originalClass implements HasCustomFieldValues {
    /**
     * Get an custom field value by name
     *
     * @param key The API key of the field being retrieved.
     */
    getCustomFieldValue(key: string) {
      return this.customFieldValues?.find(
        customFieldValue => customFieldValue.key === key
      );
    }

    /**
     * Add local custom field value without saving.
     */
    setLocalCustomFieldValue(key: string, value: any) {
      let customFieldValue = this.getCustomFieldValue(key);
      if (customFieldValue) {
        customFieldValue.value = value;
      } else {
        if (!this.customFieldValues) {
          // Avoid setting dirty flag
          this.setAttribute('customFieldValues', [], false);
        }
        customFieldValue = new CustomFieldValue({ key, value });
        this.customFieldValues.push(customFieldValue);
      }

      return customFieldValue;
    }

    /**
     * Set a custom field value on a record
     */
    async setCustomFieldValue(key: string, value: any) {
      this.setLocalCustomFieldValue(key, value);

      const { id, typename } = this;
      const fragment = new Fragment('setCustomFieldValue')
        .argument(
          'attributes',
          {
            record: { id, typename },
            key,
            value,
          },
          'SetCustomFieldValueAttributes!'
        )
        .fragment(
          CustomFieldValue.select('key', 'value').first().buildQueryFragment()
        );

      await CustomFieldValue.mutate(fragment);
    }
  };
}
